/**
 * AddSupplierDialog
 * ─────────────────
 * Compact dialog for adding a new supplier to the curated supplier
 * library. Triggered from the "+ Add Supplier" button on the invoice
 * line-items toolbar. Saved suppliers immediately appear in every
 * SupplierCombobox dropdown across the app.
 *
 * RBAC: backend allows SM / admin / super_admin. Mechanic users see
 * a 403 if they somehow get to this dialog.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Truck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddSupplierDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setPhone(''); setEmail(''); setAccount(''); setNotes(''); };

  const save = async () => {
    const n = (name || '').trim();
    if (!n) { toast.error('Supplier name is required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/suppliers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: n,
          phone: phone.trim() || null,
          email: email.trim() || null,
          account_number: account.trim() || null,
          notes: notes.trim() || null,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        // Bust the module-level supplier cache in SupplierCombobox so dropdowns
        // re-fetch on next render. The cache is read once per page load —
        // dispatching a custom event is the cleanest way to invalidate.
        window.dispatchEvent(new CustomEvent('supplier-list-changed'));
        toast.success(
          data.created
            ? `Added supplier "${data.supplier.name}" — available in dropdowns now.`
            : `Supplier "${data.supplier.name}" already exists — using existing entry.`,
        );
        reset();
        onSaved?.(data.supplier);
        onOpenChange(false);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Failed to add supplier (HTTP ${r.status})`);
      }
    } catch (_e) {
      toast.error('Network error — please retry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="add-supplier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-amber-700" />
            Add Supplier
          </DialogTitle>
          <DialogDescription>
            Adds the supplier to your workshop's list so it shows up in every
            Supplier dropdown — and the Super Admin's supplier contribution
            report picks it up automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="sup-name">Supplier name <span className="text-rose-600">*</span></Label>
            <Input
              id="sup-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Repco, Burson, SparesBox"
              data-testid="add-supplier-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="sup-phone">Phone</Label>
              <Input id="sup-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="optional" data-testid="add-supplier-phone" />
            </div>
            <div>
              <Label htmlFor="sup-email">Email</Label>
              <Input id="sup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="optional" data-testid="add-supplier-email" />
            </div>
          </div>
          <div>
            <Label htmlFor="sup-account">Account #</Label>
            <Input id="sup-account" value={account} onChange={(e) => setAccount(e.target.value)} placeholder="optional — your trade account number" data-testid="add-supplier-account" />
          </div>
          <div>
            <Label htmlFor="sup-notes">Notes</Label>
            <Textarea
              id="sup-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="optional — opening hours, delivery, rep contact, etc."
              rows={2}
              data-testid="add-supplier-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !name.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="add-supplier-save">
            {saving ? 'Saving…' : 'Save supplier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
